import React, { FunctionComponent, useReducer, useEffect, useCallback } from 'react';

import { IReducerAction } from '@dw/models/shared';
import { IUser, USER_TYPE } from '@dw/models/user';
import { useInitializeAuthMutation } from '@dw/hooks/api/AuthApi';
import { useFirebase } from '@dw/hooks';
import { useGetSelf } from '@dw/hooks/api/UserApi';
import { IDanceSchool } from '@dw/models/danceSchool';
import { useGetDanceSchool } from '@dw/hooks/api/DanceSchoolApi';
import { useQueryClient } from '@tanstack/react-query';
import { IDanceSchoolUser, USER_ROLE } from '@dw/models/danceSchoolUser';
import { IInitializeAuthResponse } from '@dw/models/auth';

type TAuthContextActions = 'SET_USER' | 'SET_DANCE_SCHOOL' | 'SET_DANCE_SCHOOL_USER' | 'SET_INITIALIZED' | 'SET_IS_AUTHENTICATING';

interface IAuthContext {
  isAuthenticated: boolean;
  isInitialized: boolean;
  isAuthenticating: boolean;
  user?: IUser;
  danceSchool?: IDanceSchool;
  danceSchoolUser?: IDanceSchoolUser;

  setAuthDetails: (authDetails: IInitializeAuthResponse) => void;
  userHasRole: (...roles: USER_ROLE[]) => boolean;
  userHasExactRole: (...roles: USER_ROLE[]) => boolean;
}
const initialAuthState: IAuthContext = {
  isAuthenticated: false,
  isInitialized: false,
  isAuthenticating: false, 
  setAuthDetails: () => {},
  userHasRole: () => false,
  userHasExactRole: () => false
};
export const AuthContext = React.createContext(initialAuthState);

const AuthContextReducer = (
  state: IAuthContext,
  action: IReducerAction<TAuthContextActions>
): IAuthContext => {
  switch (action.type) {
    case 'SET_USER':
      return { ...state, user: action.payload, isAuthenticated: !!action.payload };
    case 'SET_DANCE_SCHOOL':
      return { ...state, danceSchool: action.payload };
    case 'SET_DANCE_SCHOOL_USER':
      return { ...state, danceSchoolUser: action.payload };
    case 'SET_INITIALIZED':
      return { ...state, isInitialized: action.payload };
    case 'SET_IS_AUTHENTICATING':
      return { ...state, isAuthenticating: action.payload };
    default:
      return state;
  }
};

interface IAuthContextProviderProps {
  children: React.ReactNode;
}
export const AuthContextProvider: FunctionComponent<
  IAuthContextProviderProps
> = (props: IAuthContextProviderProps) => {
  
  const queryClient = useQueryClient();

  const {
    isAuthorized: isFirebaseAuthorized,
    authorizedUser,
    isInitialized: isFirebaseInitialized,
    forceTokenReload
  } = useFirebase();

  const [state, dispatch] = useReducer(AuthContextReducer, {
    ...initialAuthState
  });

  const {
    mutate: initializePortalAuth,
    isPending: initializePortalAuthLoading,
    isSuccess: initializePortalAuthSuccess
  } = useInitializeAuthMutation({
    onSuccess: async (response) => {
      if (response.tokenReloadRequired) {
        await forceTokenReload();
      }
      dispatch({ type: 'SET_USER', payload: response.user });
      dispatch({ type: 'SET_DANCE_SCHOOL', payload: response.activeDanceSchoolUser?.danceSchool });
      dispatch({ type: 'SET_DANCE_SCHOOL_USER', payload: response.activeDanceSchoolUser });
      dispatch({ type: 'SET_INITIALIZED', payload: true });
    },
    onError: () => {
      dispatch({ type: 'SET_USER', payload: null });
      dispatch({ type: 'SET_DANCE_SCHOOL', payload: null });
      dispatch({ type: 'SET_DANCE_SCHOOL_USER', payload: null });
      dispatch({ type: 'SET_INITIALIZED', payload: true });
    }
  });

  const { data: getSelfResult } = useGetSelf({
    enabled: isFirebaseAuthorized && initializePortalAuthSuccess,
    autoShowErrorMessages: false,
    autoLogOutOnAuthError: !!state.user
  });

  const { data: getDanceSchoolResult } = useGetDanceSchool(state.danceSchool?.id ?? '', {
    enabled: isFirebaseAuthorized && initializePortalAuthSuccess,
    autoShowErrorMessages: false,
    autoLogOutOnAuthError: !!state.user
  });

  const userHasRole = useCallback((...roles: USER_ROLE[]) => {
    return roles.some(x => x === state.danceSchoolUser?.role);
  }, [state.danceSchoolUser]);

  const userHasExactRole = useCallback((...roles: USER_ROLE[]) => {
    if (!state.danceSchoolUser?.role) {
      return false;
    }

    return roles.includes(state.danceSchoolUser.role);
  }, [state.danceSchoolUser]);

  const setAuthDetails = useCallback((authDetails: IInitializeAuthResponse) => {
    if (authDetails.user) {
      dispatch({
        type: 'SET_USER',
        payload: authDetails.user
      });
    }

    if (authDetails.activeDanceSchoolUser) {
      dispatch({
        type: 'SET_DANCE_SCHOOL',
        payload: authDetails.activeDanceSchoolUser.danceSchool
      });
      dispatch({
        type: 'SET_DANCE_SCHOOL_USER',
        payload: authDetails.activeDanceSchoolUser
      });
    }
    queryClient.clear();
  }, [dispatch, queryClient]);

  useEffect(() => {
    if (authorizedUser) {
      initializePortalAuth({
        firstName: authorizedUser?.name ?? '',
        lastName: authorizedUser?.surname ?? '',
        userType: USER_TYPE.PORTAL
      });
    } else {
      dispatch({
        type: 'SET_USER',
        payload: null,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorizedUser]);

  useEffect(() => {
    if (isFirebaseInitialized && !isFirebaseAuthorized) {
      dispatch({
        type: 'SET_INITIALIZED',
        payload: true
      });
    }
  }, [isFirebaseInitialized, isFirebaseAuthorized]);

  useEffect(() => {
    dispatch({
      type: 'SET_IS_AUTHENTICATING',
      payload: initializePortalAuthLoading
    });
  }, [initializePortalAuthLoading]);

  useEffect(() => {
    if (getSelfResult) {
      dispatch({
        type: 'SET_USER',
        payload: getSelfResult
      });

      if (window.newrelic) {
        window.newrelic?.setUserId(getSelfResult.id);
      }
    }
  }, [getSelfResult]);

  useEffect(() => {
    if (getDanceSchoolResult) {
      dispatch({
        type: 'SET_DANCE_SCHOOL',
        payload: getDanceSchoolResult
      });

      if (window.newrelic) {
        window.newrelic?.setCustomAttribute('danceSchoolId', getDanceSchoolResult.id);
      }
    }
  }, [getDanceSchoolResult]);

  return (
    <AuthContext.Provider value={{...state, setAuthDetails, userHasRole, userHasExactRole}}>{props.children}</AuthContext.Provider>
  );
};
